import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { humanizeTemplateKey } from "../mailOutboxPage.helpers";
import type { MailOutboxPageFilters } from "../mailOutboxPage.types";

interface MailOutboxToolbarProps {
  filters: MailOutboxPageFilters;
  templateKeys: string[];
  isFetching: boolean;
  onFiltersChange: (filters: Partial<MailOutboxPageFilters>) => void;
  onResetFilters: () => void;
}

export function MailOutboxToolbar({
  filters,
  templateKeys,
  isFetching,
  onFiltersChange,
  onResetFilters,
}: MailOutboxToolbarProps) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <Select
        value={filters.status ?? "__all__"}
        onValueChange={(value) =>
          onFiltersChange({
            status: value === "__all__" ? undefined : (value as MailOutboxPageFilters["status"]),
          })
        }
      >
        <SelectTrigger className="w-40">
          <SelectValue placeholder="Tous les statuts" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">Tous les statuts</SelectItem>
          <SelectItem value="sent">Envoyé</SelectItem>
          <SelectItem value="failed">Échec</SelectItem>
          <SelectItem value="pending">En attente</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.templateKey ?? "__all__"}
        onValueChange={(value) =>
          onFiltersChange({ templateKey: value === "__all__" ? undefined : value })
        }
      >
        <SelectTrigger className="w-52">
          <SelectValue placeholder="Tous les modèles" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">Tous les modèles</SelectItem>
          {templateKeys.map((key) => (
            <SelectItem key={key} value={key}>
              {humanizeTemplateKey(key)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        value={filters.recipient}
        onChange={(event) => onFiltersChange({ recipient: event.target.value })}
        placeholder="Rechercher un destinataire..."
        className="w-56"
      />

      <DatePicker
        value={filters.from}
        onChange={(value) => onFiltersChange({ from: value })}
        placeholder="Depuis"
        className="w-40"
      />
      <DatePicker
        value={filters.to}
        onChange={(value) => onFiltersChange({ to: value })}
        placeholder="Jusqu'à"
        className="w-40"
      />

      <Button variant="secondary" size="sm" onClick={onResetFilters}>
        Réinitialiser
      </Button>

      {isFetching && (
        <span className="flex items-center gap-1.5 text-[11px] text-subtle">
          <Loader2 size={12} className="animate-spin" />
          Recherche...
        </span>
      )}
    </div>
  );
}
